import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useSurveysGateway } from './SurveysGatewayProvider';
import { getPublishedSurveyBySlugQueryOptions } from '../api/subjectSurveys';

import {
  INQUIRY_SUMMARY_HELPFUL_ANSWER_FIELD,
  INQUIRY_SUMMARY_HELPFUL_SURVEY_SLUG,
  INQUIRY_SUMMARY_SUBJECT_TYPE,
} from '@/constants/inquirySummary';
import { Sentry } from '@/util/sentry';

/**
 * Records a summary's How did we do? vote as a survey response on the post, so votes can be
 * read per post in admin rather than only in analytics.
 *
 * Submission is fire-and-forget: the thumbs change at once and a failed POST goes to
 * Sentry, not to the reader, because a vote is too small an act to interrupt.
 *
 * A changed vote is a second response. The backend keeps every submission and counts
 * the newest per reader and post, and a reader cannot delete a response, so taking a
 * vote back submits nothing and the last vote stands. Analytics still records the
 * clear.
 *
 * Off while `enabled` is false. Submitting needs a signed-in, confirmed account, so a
 * guest reading through a gift link never reaches the endpoint.
 */
export function useInquirySummaryRatingSurvey({
  postId,
  enabled,
}: {
  postId: number;
  enabled: boolean;
}) {
  const queryClient = useQueryClient();

  const gateway = useSurveysGateway();

  /** Keeps the definition warm so a vote has its id ready. */
  useQuery({
    ...getPublishedSurveyBySlugQueryOptions(INQUIRY_SUMMARY_HELPFUL_SURVEY_SLUG, gateway),
    enabled,
  });

  return useCallback(
    async (verdict: 'up' | 'down') => {
      if (!enabled) {
        return;
      }

      try {
        const definition = await queryClient.ensureQueryData(
          getPublishedSurveyBySlugQueryOptions(INQUIRY_SUMMARY_HELPFUL_SURVEY_SLUG, gateway)
        );

        if (!definition) {
          return;
        }

        await gateway.submitResponse({
          surveyDefinitionId: definition.id,
          answers: { [INQUIRY_SUMMARY_HELPFUL_ANSWER_FIELD]: verdict },
          subject: { type: INQUIRY_SUMMARY_SUBJECT_TYPE, id: String(postId) },
        });
      } catch (error) {
        Sentry.captureException(error, {
          tags: { component: 'useInquirySummaryRatingSurvey', source: 'submit-vote' },
          extra: { errorMessage: (error as Error | undefined)?.message, postId },
        });
      }
    },
    [enabled, queryClient, gateway, postId]
  );
}
